import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import { createRetainer } from '@/lib/live/retainer'
import {
  type QuestionReply,
  answerArticleQuestion as repoAnswerArticleQuestion,
  subscribeLatestArticleRun,
  subscribeOpenArticleRuns,
  writePage as repoWritePage,
} from '@/repositories/article-run'
import { useKeyedSubscription } from '@/stores/keyed-subscription'
import { currentSetupScope, useScopedSubscription } from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import type { ArticleRun } from '@/types/article-run'
import { isLoaded } from '@/types/load-state'
import { isRunLive, isRunOpen, isRunWaiting } from '@/types/setup-run'

/** The brand's article runs: the latest run of the page on screen, the runs still open on any page, and the start of a new one. */
export const useArticleRunStore = defineStore('article-run', () => {
  const latest = useKeyedSubscription<ArticleRun | null>(subscribeLatestArticleRun)
  const openRuns = useScopedSubscription(subscribeOpenArticleRuns)

  const openRetainer = createRetainer({
    open() {
      const scope = currentSetupScope()
      if (scope) openRuns.open(scope)
      else openRuns.fail(i18n.global.t('common.noWorkspaceSelected'))
    },
    close() {
      openRuns.close()
    },
  })
  onScopeDispose(openRetainer.dispose)

  /** The pages an open run is writing; empty until the feed answers. */
  const openRunPageIds = computed<ReadonlySet<string>>(() => {
    const state = openRuns.state.value
    return new Set(isLoaded(state) ? state.value.map((entry) => entry.pageId) : [])
  })

  const starting = ref(false)

  const run = computed(() => (isLoaded(latest.state.value) ? latest.state.value.value : null))
  const isLive = computed(() => isRunLive(run.value))
  const isWaiting = computed(() => isRunWaiting(run.value))
  const isOpen = computed(() => isRunOpen(run.value))
  const settled = computed(() => latest.state.value.status !== 'loading')
  const answering = ref<string | null>(null)

  /** Opens an article run for a page; `false` on failure (toasted). */
  async function write(pageId: string): Promise<boolean> {
    starting.value = true
    try {
      await repoWritePage(pageId)
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('errors.somethingWentWrong'),
        parseCallableError(cause).message,
      )
      return false
    } finally {
      starting.value = false
    }
  }

  /** Records the company's reply to one question of the run on screen; `false` on failure (toasted). */
  async function answerQuestion(
    runId: string,
    questionId: string,
    reply: QuestionReply,
  ): Promise<boolean> {
    answering.value = questionId
    try {
      await repoAnswerArticleQuestion(runId, questionId, reply)
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('errors.somethingWentWrong'),
        parseCallableError(cause).message,
      )
      return false
    } finally {
      answering.value = null
    }
  }

  return {
    run,
    isLive,
    isWaiting,
    isOpen,
    settled,
    starting,
    answering,
    openRunPageIds,
    retainFor: latest.retain,
    retainOpen: openRetainer.retain,
    write,
    answerQuestion,
  }
})
